/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { saveAll, saveMany } from '../store/items';
import { ItemShort } from '../components/item/item.Short';
import { usePrevious } from '../hooks/usePrevious';
import {
  setCurrentTab,
  setDrawerOpen,
  setHistory,
  setListCampaignsId,
  setOpenModales,
  setSidebarOpen,
  setTheme,
} from '../store/global';
import { ItemLarge } from '../components/item/item.Large';
import {
  getId,
  canAccessItem,
  getItemPicAsset,
} from '../utils/item';
import { canUseMjForItem, isMjForItem } from '../utils/auth';
import { NavigationPassword } from '../components/navigation/navigation.Passwords';
import { isEditable } from '../utils/roles';
import { NavigationGlobal } from '../components/navigation/navigation.Global';
import {
  getStorageJSON,
  getStorageValue,
  setStorageJSON,
} from '../utils/userStorage';
import { LoadingOverlay } from '../components/loader/loading.Overlay';
import { updateDocumentMetadata } from '../utils/documentMetadata';

function Item() {
  const params = useParams();
  const item = useSelector(state => state.items[params.id]);
  const campaign = useSelector(state => state.items[item?.campaign]);
  const world = useSelector(state => state.items[item?.world]);
  const universe = useSelector(state => state.items[item?.universe]);
  const drawer = useSelector(state => state.global.drawer);
  const isMobile = useSelector(state => state.global.isMobile);
  const items = useSelector(state => state.items);
  const navigation = useSelector(state => state.global.navigation);
  const currentTab = useSelector(state => state.global.currentTab);
  const dispatch = useDispatch();
  const previousParamId = usePrevious(params.id);

  const [loadedCampaign, setLoadedCampaign] = useState(false);
  const [loadedWorld, setLoadedWorld] = useState(false);
  const [loadedUniverse, setLoadedUniverse] = useState(false);
  const [fetchPulse, setFetchPulse] = useState(0);
  const isValidObjectId = id => /^[a-f0-9]{24}$/i.test(id);
  const noneVisitor = process.env.REACT_APP_NONE_VISITOR || 'none-visitor';
  const inflightFetchesRef = useRef(new Set());
  const fetchedOnceRef = useRef(new Set());
  const lastFetchAtRef = useRef(new Map());
  const lastRelatedBatchKeyRef = useRef(null);
  const lastGridWidgetsBatchKeyRef = useRef(null);
  const lastForcedRouteFetchRef = useRef(null);
  const lastForcedTabFetchRef = useRef(null);
  const lastCampaignUniverseFetchRef = useRef(null);
  const requestedRelatedIdsRef = useRef(new Set());
  const requestedGridIdsRef = useRef(new Set());
  const [showHydrationLoader, setShowHydrationLoader] = useState(true);
  const hydrationStartRef = useRef(Date.now());
  const [hydrationStartedAt, setHydrationStartedAt] = useState(Date.now());

  const getItems = async (itemId, options = {}) => {
    const force = options?.force === true;
    if (!itemId || !isValidObjectId(itemId)) {
      return;
    }
    if (inflightFetchesRef.current.has(itemId)) {
      return;
    }
    if (!force && fetchedOnceRef.current.has(itemId)) {
      const existing = items?.[itemId];
      if (existing && existing.__partial !== true) {
        return;
      }
    }
    const now = Date.now();
    const lastFetch = lastFetchAtRef.current.get(itemId);
    if (!force && lastFetch && now - lastFetch < 2000) {
      return;
    }
    lastFetchAtRef.current.set(itemId, now);
    inflightFetchesRef.current.add(itemId);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/items/batch`,
        { ids: [itemId] },
      );
      const fetchedItem = Array.isArray(res?.data)
        ? res.data.find(entry => entry?._id === itemId)
        : null;
      if (fetchedItem?._id) {
        dispatch(saveAll({ item: fetchedItem }));
      }
      fetchedOnceRef.current.add(itemId);
    } catch (error) {
      // Keep silent here: callers handle missing/inaccessible items.
    } finally {
      inflightFetchesRef.current.delete(itemId);
      setFetchPulse(value => value + 1);
    }
  };

  useEffect(() => {
    if (!params?.id || !isValidObjectId(params.id)) {
      return;
    }
    requestedRelatedIdsRef.current.clear();
    requestedGridIdsRef.current.clear();
    lastRelatedBatchKeyRef.current = null;
    lastGridWidgetsBatchKeyRef.current = null;
    lastForcedTabFetchRef.current = null;
    if (lastForcedRouteFetchRef.current === params.id) {
      return;
    }
    lastForcedRouteFetchRef.current = params.id;
    // Always refresh route item from server when entering its page,
    // even if it already exists in local store (avoids stale cache overwrite issues).
    getItems(params.id, { force: true });
  }, [params?.id]);

  useEffect(() => {
    if (!item?._id || !currentTab || !isValidObjectId(currentTab)) {
      return;
    }
    const tabIds = Array.isArray(item?.tabs)
      ? item.tabs.map(tabObj => getId(tabObj))
      : [];
    if (!tabIds.includes(currentTab)) {
      return;
    }
    const forcedTabKey = `${item._id}:${currentTab}`;
    if (lastForcedTabFetchRef.current === forcedTabKey) {
      return;
    }
    lastForcedTabFetchRef.current = forcedTabKey;
    // Refresh active tab on tab switch / route return to avoid stale grid cache.
    getItems(currentTab, { force: true });
  }, [item?._id, item?.tabs, currentTab]);

  // GET CURRENT ITEM
  useEffect(() => {
    if (
      (!item || item?.__partial) &&
      params?.id &&
      isValidObjectId(params.id)
    ) {
      getItems(params.id);
    }
  }, [item, params]);

  useEffect(() => {
    const universeId =
      item?.type === 'universe' ? item?._id : item?.universe ?? null;
    const hasMjAccess =
      isMjForItem(item, items) || canUseMjForItem(item, items);
    if (!isValidObjectId(universeId)) {
      dispatch(setListCampaignsId([]));
      return;
    }
    const fetchKey = `${universeId}:${hasMjAccess ? 'mj' : 'std'}`;
    if (lastCampaignUniverseFetchRef.current === fetchKey) {
      return;
    }
    lastCampaignUniverseFetchRef.current = fetchKey;

    const fetchCampaignAccess = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/items/allcampaigns/${universeId}`,
        );
        const campaigns = Array.isArray(res?.data) ? res.data : [];
        if (campaigns.length > 0) {
          dispatch(saveMany({ items: campaigns, log: 'page-item-campaigns' }));
        }

        const universeForAccess =
          item?.type === 'universe' && item?._id === universeId
            ? item
            : items?.[universeId] || universe;

        dispatch(
          setListCampaignsId(
            campaigns
              .filter(campaignItem => {
                if (!campaignItem?._id) {
                  return false;
                }
                if (hasMjAccess) {
                  return true;
                }
                return canAccessItem(
                  campaignItem,
                  campaignItem,
                  null,
                  universeForAccess,
                );
              })
              .map(campaignItem => campaignItem._id),
          ),
        );
      } catch (error) {
        dispatch(setListCampaignsId([]));
      }
    };

    fetchCampaignAccess();
  }, [item?._id, item?.type, item?.universe, items, universe, dispatch]);

  // GET ITEM'S LINKS ADN TABS
  useEffect(() => {
    if (items?.[item?._id]) {
      const canMj = isMjForItem(item, items) || canUseMjForItem(item, items);
      if (item && canMj === false && isEditable() === true) {
        if (!loadedCampaign || !loadedWorld || !loadedUniverse) {
          return;
        }
        const isValid = canAccessItem(item, campaign, world, universe);
        if (isValid === false) {
          dispatch(setOpenModales({ id: 'passwords', open: true }));
        } else {
          dispatch(setOpenModales({ id: 'passwords', open: false }));
        }
      }
    }
  }, [
    item,
    campaign,
    world,
    universe,
    items,
    loadedCampaign,
    loadedWorld,
    loadedUniverse,
  ]);

  // Batch prefetch links/tabs to avoid long "default values" phase
  // for formula-based nums while related items are still loading.
  useEffect(() => {
    if (!item?._id) {
      return;
    }
    const idsToFetch = new Set();
    const parentIds = [item?.campaign, item?.world, item?.universe];
    parentIds.forEach(parentId => {
      if (!isValidObjectId(parentId)) {
        return;
      }
      if (requestedRelatedIdsRef.current.has(parentId)) {
        return;
      }
      const existing = items?.[parentId];
      if (!existing || existing.__partial) {
        idsToFetch.add(parentId);
      }
    });
    if (Array.isArray(item?.links)) {
      item.links.forEach(linkObj => {
        const linkId = getId(linkObj);
        if (!isValidObjectId(linkId)) {
          return;
        }
        if (requestedRelatedIdsRef.current.has(linkId)) {
          return;
        }
        const existing = items?.[linkId];
        if (!existing || existing.__partial) {
          idsToFetch.add(linkId);
        }
      });
    }
    if (Array.isArray(item?.tabs)) {
      item.tabs.forEach(tabObj => {
        const tabId = getId(tabObj);
        if (!isValidObjectId(tabId)) {
          return;
        }
        if (requestedRelatedIdsRef.current.has(tabId)) {
          return;
        }
        const existing = items?.[tabId];
        if (!existing || existing.__partial) {
          idsToFetch.add(tabId);
        }
      });
    }
    const ids = Array.from(idsToFetch).sort();
    if (ids.length === 0) {
      return;
    }
    const batchKey = `${item._id}:${ids.join(',')}`;
    if (lastRelatedBatchKeyRef.current === batchKey) {
      return;
    }
    lastRelatedBatchKeyRef.current = batchKey;

    const fetchBatch = async () => {
      ids.forEach(id => requestedRelatedIdsRef.current.add(id));
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/items/batch`,
          { ids },
        );
        if (Array.isArray(res?.data) && res.data.length > 0) {
          res.data.forEach(batchItem => {
            if (batchItem?._id) {
              fetchedOnceRef.current.add(batchItem._id);
            }
          });
          dispatch(
            saveMany({ items: res.data, log: 'page-item-related-batch' }),
          );
        }
        // Consider this dependency round as resolved even if some ids are absent
        // from payload (legacy/deleted/inaccessible references).
        ids.forEach(id => fetchedOnceRef.current.add(id));
      } catch (error) {
        ids.forEach(id => requestedRelatedIdsRef.current.delete(id));
        // fallback silently to existing per-item fetch flow
      } finally {
        setFetchPulse(value => value + 1);
      }
    };
    fetchBatch();
  }, [item?._id, item?.links, item?.tabs, items, dispatch]);

  // Batch prefetch items referenced by widgets in active tab grid.
  useEffect(() => {
    const currentActiveTab =
      currentTab && isValidObjectId(currentTab) ? items?.[currentTab] : null;
    if (!item?._id || !currentActiveTab?._id || !Array.isArray(currentActiveTab?.grid)) {
      return;
    }
    const idsToFetch = new Set();
    currentActiveTab.grid.forEach(gridObj => {
      const elementId = getId(gridObj);
      const gridElement = gridObj?.[elementId];
      if (!gridElement) {
        return;
      }

      const ownerId = gridElement?.item;
      if (ownerId && isValidObjectId(ownerId)) {
        if (requestedGridIdsRef.current.has(ownerId)) {
          return;
        }
        const existingOwner = items?.[ownerId];
        if (!existingOwner || existingOwner.__partial) {
          idsToFetch.add(ownerId);
        }
      }

      if (gridElement?.type === 'link' && isValidObjectId(elementId)) {
        if (requestedGridIdsRef.current.has(elementId)) {
          return;
        }
        const existingLinkTarget = items?.[elementId];
        if (!existingLinkTarget || existingLinkTarget.__partial) {
          idsToFetch.add(elementId);
        }
      }

      if (
        (gridElement?.type === 'timeline' ||
          gridElement?.type === 'timelines') &&
        ownerId &&
        items?.[ownerId]?.timelines
      ) {
        const timelineEntry = items[ownerId].timelines.find(
          entry => getId(entry) === elementId,
        );
        const timelineData = timelineEntry?.[elementId];
        if (Array.isArray(timelineData?.entries)) {
          timelineData.entries.forEach(entry => {
            if (entry?.itemId && isValidObjectId(entry.itemId)) {
              if (requestedGridIdsRef.current.has(entry.itemId)) {
                return;
              }
              const existingTimelineItem = items?.[entry.itemId];
              if (!existingTimelineItem || existingTimelineItem.__partial) {
                idsToFetch.add(entry.itemId);
              }
            }
          });
        }
      }
    });

    const ids = Array.from(idsToFetch).sort();
    if (ids.length === 0) {
      return;
    }
    const batchKey = `${item._id}:${currentActiveTab._id}:grid:${ids.join(',')}`;
    if (lastGridWidgetsBatchKeyRef.current === batchKey) {
      return;
    }
    lastGridWidgetsBatchKeyRef.current = batchKey;

    const fetchGridWidgetsBatch = async () => {
      ids.forEach(id => requestedGridIdsRef.current.add(id));
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/items/batch`,
          { ids },
        );
        if (Array.isArray(res?.data) && res.data.length > 0) {
          res.data.forEach(batchItem => {
            if (batchItem?._id) {
              fetchedOnceRef.current.add(batchItem._id);
            }
          });
          dispatch(
            saveMany({ items: res.data, log: 'page-item-grid-widgets-batch' }),
          );
        }
        // Consider this dependency round as resolved even if some ids are absent
        // from payload (legacy/deleted/inaccessible references).
        ids.forEach(id => fetchedOnceRef.current.add(id));
      } catch (error) {
        ids.forEach(id => requestedGridIdsRef.current.delete(id));
        // keep fallback behavior
      } finally {
        setFetchPulse(value => value + 1);
      }
    };
    fetchGridWidgetsBatch();
  }, [item?._id, currentTab, items, dispatch]);

  // CLOSE DRAWER WHEN URL CHANGE
  useEffect(() => {
    if (params?.id !== previousParamId) {
      dispatch(setDrawerOpen(null));
      dispatch(setHistory(params.id));
      const currentRecent = getStorageJSON('recentItemIds', []);
      if (Array.isArray(currentRecent)) {
        const nextRecent = Array.from(
          new Set([params.id, ...currentRecent]),
        ).slice(0, 100);
        setStorageJSON('recentItemIds', nextRecent);
      }
    }
  }, [params?.id]);

  // CLOSE DRAWER IF ITEM DOESN'T EXIST
  useEffect(() => {
    if (items[drawer.open] === undefined) {
      dispatch(setDrawerOpen(null));
    }
  }, [items]);

  // SET CURRENT TAB
  useEffect(() => {
    if (item?.tabs?.length) {
      const tabIds = item.tabs.map(tabObj => getId(tabObj));
      const firstTab = tabIds[0];
      const itemChanged = item?._id && item?._id !== previousParamId;
      const currentInItem = currentTab ? tabIds.includes(currentTab) : false;
      if (firstTab && (itemChanged || !currentInItem)) {
        dispatch(setCurrentTab(firstTab));
      }
    }
    if (isEditable() === false) {
      dispatch(setSidebarOpen(false));
    }
  }, [
    item?._id,
    item?.tabs,
    currentTab,
    previousParamId,
  ]);

  useEffect(() => {
    const avatarAsset = getItemPicAsset(item, 0);
    updateDocumentMetadata({
      title: item?.label || 'Loading...',
      description: '',
      image: avatarAsset?.url || null,
      type: 'article',
    });
  }, [
    item,
    item?.label,
    item?.pics,
  ]);

  useEffect(() => {
    setLoadedCampaign(false);
    setLoadedWorld(false);
    setLoadedUniverse(false);
  }, [params?.id]);

  useEffect(() => {
    setShowHydrationLoader(true);
    const now = Date.now();
    hydrationStartRef.current = now;
    setHydrationStartedAt(now);
  }, [params?.id]);

  useEffect(() => {
    const isParentResolved = (parentId, parentObj) => {
      if (!item?._id) return false;
      if (!parentId || parentId === noneVisitor || !isValidObjectId(parentId)) {
        return true;
      }
      if (parentObj?._id) {
        return true;
      }
      // Parent was fetched but API returned empty payload (legacy/inaccessible/deleted):
      // treat as resolved so page doesn't stay stuck on loading forever.
      return fetchedOnceRef.current.has(parentId);
    };

    setLoadedCampaign(isParentResolved(item?.campaign, campaign));
    setLoadedWorld(isParentResolved(item?.world, world));
    setLoadedUniverse(isParentResolved(item?.universe, universe));
  }, [item, campaign, world, universe, noneVisitor, fetchPulse]);

  useEffect(() => {
    if (item?.theme?.[0]) {
      dispatch(setTheme(item.theme[0]));
    } else {
      const currentTheme = getStorageValue('currentTheme');
      if (currentTheme) {
        dispatch(
          setTheme(JSON.parse(window.localStorage.getItem(currentTheme))),
        );
      }
    }
  }, [item?.theme]);

  const directDependencyIds = useMemo(() => {
    if (!item?._id) {
      return [];
    }
    const ids = new Set([item._id]);
    if (isValidObjectId(item?.campaign)) {
      ids.add(item.campaign);
    }
    if (isValidObjectId(item?.world)) {
      ids.add(item.world);
    }
    if (isValidObjectId(item?.universe)) {
      ids.add(item.universe);
    }
    if (Array.isArray(item?.links)) {
      item.links.forEach(linkObj => {
        const id = getId(linkObj);
        if (isValidObjectId(id)) {
          ids.add(id);
        }
      });
    }
    if (Array.isArray(item?.tabs)) {
      item.tabs.forEach(tabObj => {
        const id = getId(tabObj);
        if (isValidObjectId(id)) {
          ids.add(id);
        }
      });
    }
    return Array.from(ids);
  }, [
    item?._id,
    item?.campaign,
    item?.world,
    item?.universe,
    item?.links,
    item?.tabs,
  ]);

  const activeTab = useMemo(() => {
    if (!currentTab) {
      return null;
    }
    return items?.[currentTab] || null;
  }, [currentTab, items]);

  const activeTabDependencyIds = useMemo(() => {
    const ids = new Set();
    if (!activeTab?._id || !Array.isArray(activeTab?.grid)) {
      return [];
    }
    ids.add(activeTab._id);
    activeTab.grid.forEach(gridObj => {
      const elementId = getId(gridObj);
      const gridElement = gridObj?.[elementId];
      if (!gridElement) {
        return;
      }
      if (gridElement?.item && isValidObjectId(gridElement.item)) {
        ids.add(gridElement.item);
      }
      if (gridElement?.type === 'link' && isValidObjectId(elementId)) {
        ids.add(elementId);
      }
      if (gridElement?.type === 'filter') {
        const filterEntry = items?.[gridElement.item]?.filters?.find(
          filterObj => getId(filterObj) === elementId,
        );
        const filter = filterEntry?.[elementId];
        if (Array.isArray(filter?.items)) {
          filter.items.forEach(id => {
            if (isValidObjectId(id)) {
              ids.add(id);
            }
          });
        }
        if (Array.isArray(filter?.exclude)) {
          filter.exclude.forEach(id => {
            if (isValidObjectId(id)) {
              ids.add(id);
            }
          });
        }
      }
      if (
        (gridElement?.type === 'timeline' ||
          gridElement?.type === 'timelines') &&
        gridElement?.item &&
        items?.[gridElement.item]?.timelines
      ) {
        const timelineEntry = items[gridElement.item].timelines.find(
          entry => getId(entry) === elementId,
        );
        const timelineData = timelineEntry?.[elementId];
        if (Array.isArray(timelineData?.entries)) {
          timelineData.entries.forEach(entry => {
            if (entry?.itemId && isValidObjectId(entry.itemId)) {
              ids.add(entry.itemId);
            }
          });
        }
      }
    });
    return Array.from(ids);
  }, [activeTab?._id, activeTab?.grid, items]);

  const strictResolvedIdsSet = useMemo(() => {
    return new Set(activeTabDependencyIds);
  }, [activeTabDependencyIds]);

  const areDirectDependenciesResolved = useMemo(() => {
    if (!item?._id) {
      return false;
    }
    const allDependencyIds = Array.from(
      new Set([...directDependencyIds, ...activeTabDependencyIds]),
    );
    return allDependencyIds.every(id => {
      const existing = items?.[id];
      if (existing && existing.__partial !== true) {
        return true;
      }
      if (strictResolvedIdsSet.has(id)) {
        return false;
      }
      return fetchedOnceRef.current.has(id);
    });
  }, [
    item?._id,
    directDependencyIds,
    activeTabDependencyIds,
    strictResolvedIdsSet,
    items,
    fetchPulse,
  ]);

  useEffect(() => {
    if (!item?._id || !loadedCampaign || !loadedWorld || !loadedUniverse) {
      return;
    }
    if (Array.isArray(item?.tabs) && item.tabs.length > 0 && !activeTab?._id) {
      const hasPendingTabResolution = item.tabs.some(tabObj => {
        const tabId = getId(tabObj);
        if (!isValidObjectId(tabId)) {
          return false;
        }
        const existingTab = items?.[tabId];
        if (existingTab && existingTab.__partial !== true) {
          return false;
        }
        return !fetchedOnceRef.current.has(tabId);
      });
      if (hasPendingTabResolution) {
        return;
      }
    }
    if (!areDirectDependenciesResolved) {
      return;
    }
    const minLoaderMs = 700;
    const elapsed = Date.now() - hydrationStartRef.current;
    const delay = Math.max(0, minLoaderMs - elapsed);
    const timeout = window.setTimeout(() => {
      setShowHydrationLoader(false);
    }, delay);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [
    item?._id,
    loadedCampaign,
    loadedWorld,
    loadedUniverse,
    item?.tabs,
    activeTab?._id,
    areDirectDependenciesResolved,
    items,
    fetchPulse,
  ]);

  if (item?._id) {
    const canMj = isMjForItem(item, items) || canUseMjForItem(item, items);
    const content = isMobile ? (
      <div className='relative overflow-hidden h-full'>
        {canMj === false && (
          <NavigationPassword
            itemId={params.id}
            forceOpen={true}
          />
        )}
        <ItemShort
          displayType={'sidebar'}
          itemId={params.id}
          disableSelfFetch={true}
        />
        {navigation.open && (
          <NavigationGlobal
            itemId={params.id}
            hideBottomNav={true}
            style={{
              position: 'absolute',
              top: 0,
              right: navigation.open ? 0 : -navigation.size,
              zIndex: 20,
            }}
          />
        )}
      </div>
    ) : (
      <div className='flex bg-main text-contrast'>
        {canMj === false && (
          <NavigationPassword
            itemId={params.id}
            forceOpen={true}
          />
        )}
        <ItemShort
          displayType={'sidebar'}
          itemId={params.id}
          disableSelfFetch={true}
        />
        <ItemLarge
          itemId={params.id}
          disableSelfFetch={true}
        />
      </div>
    );

    return (
      <div
        className='relative min-h-screen overflow-hidden'
        style={{ height: '100dvh', minHeight: '100dvh' }}
      >
        {content}
        {showHydrationLoader && (
          <LoadingOverlay
            label="Chargement de l'item..."
            itemLabel={item?.label}
            avatarAsset={getItemPicAsset(item, 0)}
            bannerAsset={getItemPicAsset(item, 1)}
            startedAtMs={hydrationStartedAt}
          />
        )}
      </div>
    );
  } else {
    return (
      <LoadingOverlay
        label="Chargement de l'item..."
        itemLabel=''
        avatarAsset={null}
        bannerAsset={null}
        startedAtMs={hydrationStartedAt}
      />
    );
  }
}

export default Item;
